/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "@design-lab/shared/ui";
import { cn } from "tailwind-variants";
import { pluralize } from "common/utils/format/word";
import { notifyError } from "common/utils/app-monitoring/notify";
import { useCanvaDesigns } from "../api/useCanvaDesigns";
import { useCanvaSelectDesign } from "../api/useCanvaSelectDesign";
import { useMultipleCanvaDesignStatus } from "./useMultipleCanvaDesignStatus";

type CanvaModalProps = {
  closeModal: () => void;
  onFilesReady: (files: File[]) => Promise<void>;
  allowMultipleSelection?: boolean;
  maxSelectionCount?: number;
};

export function CanvaModal({
  closeModal,
  onFilesReady,
  allowMultipleSelection = false,
  maxSelectionCount = 10
}: CanvaModalProps) {
  const { data, isLoading, error } = useCanvaDesigns();
  const { mutateAsync: selectDesign } = useCanvaSelectDesign();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploadingJobIds, setUploadingJobIds] = useState<string[]>([]);
  const [processedJobIds, setProcessedJobIds] = useState<Set<string>>(new Set());

  const { completedJobs, hasAllCompleted } = useMultipleCanvaDesignStatus(uploadingJobIds);

  const designs = data?.items ?? [];
  const isUploading = uploadingJobIds.length > 0;

  const handleToggleDesign = (designId: string) => {
    setSelectedIds(prev => {
      const newSelected = new Set(prev);

      if (allowMultipleSelection) {
        // Multiple selection mode
        if (newSelected.has(designId)) {
          newSelected.delete(designId);
        } else if (newSelected.size < maxSelectionCount) {
          newSelected.add(designId);
        }
      } else {
        // Single selection mode - clear previous and select new one
        newSelected.clear();
        newSelected.add(designId);
      }

      return newSelected;
    });
  };

  const handleFilesReady = useCallback(
    async (urls: string[]) => {
      const files = await Promise.all(
        urls.map(async url => {
          const response = await fetch(url);
          const blob = await response.blob();

          const urlPath = new URL(url).pathname;
          const fileName = urlPath.substring(urlPath.lastIndexOf("/") + 1);
          return new File([blob], fileName, { type: blob.type });
        })
      );

      await onFilesReady(files);

      closeModal();
    },
    [onFilesReady, closeModal]
  );

  const handleProceed = async () => {
    if (selectedIds.size === 0) return;

    const selectedIdsArray = Array.from(selectedIds);

    // Start all design selections in parallel
    const jobPromises = selectedIdsArray.map(async designId => {
      try {
        const response = await selectDesign({ designId });
        return response.job.id || null;
      } catch (err) {
        console.error(`Failed to select design ${designId}:`, err);
        notifyError(`Failed to select design ${designId}`, {
          params: {
            error: err instanceof Error ? err.message : String(err),
            error_object: err instanceof Error ? err.toString() : String(err),
            stack_trace: err instanceof Error ? err.stack : null,
            component: "CanvaModal",
            action: "selectDesign",
            designId
          }
        });
        return null;
      }
    });

    const jobResults = await Promise.all(jobPromises);
    const jobIds = jobResults.filter((id): id is string => id !== null);

    setUploadingJobIds(jobIds);
  };

  // Handle completed jobs
  useEffect(() => {
    if (!isUploading || completedJobs.length === 0) return;

    const newlyCompleted = completedJobs.filter(
      job => job.jobId && !processedJobIds.has(job.jobId)
    );

    if (newlyCompleted.length > 0) {
      // Mark these jobs as processed
      setProcessedJobIds(prev => {
        const newSet = new Set(prev);
        newlyCompleted.forEach(job => {
          if (job.jobId) newSet.add(job.jobId);
        });
        return newSet;
      });

      // If all jobs are complete, process all files at once
      if (hasAllCompleted) {
        const urls = completedJobs.map(job => job.url).filter(Boolean) as string[];
        handleFilesReady(urls);
      }
    }
  }, [completedJobs, hasAllCompleted, processedJobIds, handleFilesReady, isUploading]);

  return (
    <Modal
      isOpen={true}
      onClose={closeModal}
      className="flex h-[96dvh] w-[92vw] flex-col"
      title="Choose image from Canva"
      description="Select a design to import into your project"
    >
      <ModalHeader onClose={closeModal}>
        <h3 className="text-lg font-semibold">
          Choose {allowMultipleSelection ? pluralize(1, "image", "s", true) : "image"} you want to
          upload:
        </h3>
        {allowMultipleSelection && selectedIds.size > 0 && (
          <p className="mt-1 text-sm text-gray-600">
            {selectedIds.size} of {maxSelectionCount} selected
          </p>
        )}
      </ModalHeader>

      <ModalBody className="flex-1 overflow-auto p-0">
        {isLoading && !designs.length && (
          <div className="flex h-full items-center justify-center">
            <p className="text-gray-600">Loading...</p>
          </div>
        )}

        {error && (
          <div className="flex h-full items-center justify-center">
            <p className="text-red-600">Something went wrong, please try again.</p>
          </div>
        )}

        {!isLoading && !error && !designs.length && (
          <div className="flex h-full items-center justify-center">
            <p className="text-gray-600">No images found</p>
          </div>
        )}

        {designs.length > 0 && (
          <div
            className={cn(
              "grid grid-cols-3 gap-3 p-4 md:grid-cols-6",
              isUploading && "pointer-events-none opacity-50"
            )}
          >
            {designs.map(design => (
              <button
                key={design.id}
                data-testid={`canva-design-${design.id}-button`}
                className={cn(
                  "relative aspect-square cursor-pointer transition-all",
                  "hover:opacity-90",
                  selectedIds.has(design.id) && "ring-2 ring-blue-700 ring-offset-2"
                )}
                onClick={() => handleToggleDesign(design.id)}
                disabled={
                  isUploading ||
                  (!selectedIds.has(design.id) &&
                    allowMultipleSelection &&
                    selectedIds.size >= maxSelectionCount)
                }
              >
                {design.thumbnail?.url ? (
                  <img
                    src={design.thumbnail.url}
                    alt={design.title || "Untitled Design"}
                    className="size-full rounded object-cover"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center rounded bg-gray-200">
                    <span className="text-xs text-gray-400">No preview</span>
                  </div>
                )}
                {selectedIds.has(design.id) && (
                  <div className="bg-background absolute right-2 top-2 rounded-full p-0.5">
                    <CheckCircle2 className="size-6 text-blue-700" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex justify-end">
        <Button
          data-testid="canva-proceed-button"
          size="xl"
          onPress={handleProceed}
          isDisabled={selectedIds.size === 0 || isUploading}
        >
          {(() => {
            if (isUploading) {
              return "Uploading...";
            }
            if (allowMultipleSelection && selectedIds.size > 0) {
              return `Select ${pluralize(selectedIds.size, "Design", "s", true)}`;
            }
            return "Proceed";
          })()}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
